import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { ArrowRight, ArrowUpRight, Check } from '@phosphor-icons/react';

const Reveal = ({ children, delay = 0, className = '' }) => {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 14 }} animate={v ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.55, delay, ease: [0.22, 0.03, 0.26, 1] }} className={className}>
      {children}
    </motion.div>
  );
};

const Shot = ({ src, alt }) => (
  <div className="rounded-xl overflow-hidden ring-1 ring-black/[0.06]" style={{ boxShadow: '0 4px 6px rgba(0,0,0,0.04), 0 16px 40px rgba(0,0,0,0.08)' }}>
    <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" />
  </div>
);

const Label = ({ children }) => (
  <p className="text-[11px] text-slate-300 tracking-[0.2em] uppercase font-medium mb-4">{children}</p>
);

const Heading = ({ children, className = '' }) => (
  <h2 className={`text-[1.75rem] sm:text-[2rem] md:text-[2.5rem] lg:text-[2.8rem] font-bold tracking-[-0.02em] leading-[1.08] ${className}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h2>
);

const Bullet = ({ children }) => (
  <div className="flex items-start gap-3">
    <div className="w-[5px] h-[5px] rounded-full bg-[#235347] mt-[7px] flex-shrink-0" />
    <p className="text-[13px] md:text-[14px] text-slate-500 leading-relaxed">{children}</p>
  </div>
);


const PresentationPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white text-[#0F172A] overflow-x-hidden antialiased">

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-black/[0.04]">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12 h-[60px] flex items-center justify-between">
          <button onClick={() => navigate('/')}>
            <img src="/kindra-logo.png" alt="Kindra" className="h-[22px] md:h-[26px] object-contain" />
          </button>
          <a href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Inquiry" className="bg-[#0F172A] text-white text-[13px] px-5 py-[9px] rounded-lg font-medium hover:bg-slate-800 transition-colors">
            Get in touch
          </a>
        </div>
      </nav>


      {/* ═══ 1. HERO ═══ */}
      <section className="pt-[120px] md:pt-[140px] lg:pt-[160px] pb-16 md:pb-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 items-start">
            <div className="lg:pt-6 xl:pt-10">
              <Reveal>
                <Heading>Healthcare stops between appointments. <span className="text-[#235347]">Kindra doesn't.</span></Heading>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-6 lg:mt-8 text-[15px] md:text-base text-slate-400 leading-[1.7] max-w-[400px]">
                  A personal AI health companion that helps people understand, manage, and act on their health every day.
                </p>
              </Reveal>
              <Reveal delay={0.14}>
                <div className="mt-8 lg:mt-10 flex flex-wrap items-center gap-3">
                  <button onClick={() => document.getElementById('s-daily')?.scrollIntoView({ behavior: 'smooth' })} className="bg-[#0F172A] text-white pl-5 pr-4 py-[11px] rounded-lg text-[13px] font-medium hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                    See the product <ArrowRight size={14} />
                  </button>
                  <button onClick={() => document.getElementById('s-commercial')?.scrollIntoView({ behavior: 'smooth' })} className="text-slate-400 px-4 py-[11px] text-[13px] font-medium hover:text-slate-600 transition-colors">
                    Investment opportunity
                  </button>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-12 lg:mt-16 text-[11px] text-slate-300 tracking-[0.12em] uppercase">
                  Live in production &nbsp;&middot;&nbsp; Real user data &nbsp;&middot;&nbsp; Personalised output
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <Shot src="/pres-assets/hero_daily.png" alt="Kindra daily intelligence — personalised AI insights connecting Bipolar I, ADHD, cardiac history, and medication interactions with safety signals" />
            </Reveal>
          </div>
        </div>
      </section>


      {/* ═══ 2. PROBLEM + SYSTEM ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          {/* Problem */}
          <Reveal><Label>The problem</Label></Reveal>
          <Reveal delay={0.04}>
            <Heading className="max-w-xl">People are left alone between appointments.</Heading>
          </Reveal>
          <div className="mt-14 md:mt-20 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-10 lg:gap-x-14 gap-y-8">
            {[
              ['Fragmented information', 'Health data is scattered across clinics, portals, and memory. No single system holds the full picture.'],
              ['No continuity', 'Months pass between visits. Symptoms change, medications shift, concerns build — nothing is recorded.'],
              ['Poor understanding', 'Patients manage conditions they don\'t fully understand, with no guidance between visits.'],
              ['No daily support', 'Doctors are available for minutes. The rest of the time, people are managing alone.'],
            ].map(([t, d], i) => (
              <Reveal key={i} delay={0.05 * i}>
                <h3 className="text-[14px] font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h3>
                <p className="text-[13px] text-slate-400 leading-[1.7]">{d}</p>
              </Reveal>
            ))}
          </div>

          {/* System */}
          <div className="mt-20 md:mt-28 border-t border-black/[0.04] pt-20 md:pt-28">
            <Reveal><Label>The system</Label></Reveal>
            <Reveal delay={0.04}>
              <Heading className="max-w-2xl">Kindra is not a tracker or a chatbot. It is a personal health intelligence system.</Heading>
            </Reveal>
            <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-x-14 gap-y-8">
              {[
                ['Understands your health context', 'Conditions, medications, symptoms, documents, care team — structured into one persistent profile that accumulates over time.'],
                ['Guides daily decisions', 'AI-generated insights, personalised prompts, and context-aware chat that references your full health picture in every response.'],
                ['Becomes more valuable over time', 'Day one is useful. Month three reveals patterns. Year one, it knows more about your health trajectory than any single provider.'],
              ].map(([t, d], i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <h3 className="text-[15px] font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h3>
                  <p className="text-[13px] text-slate-400 leading-[1.7]">{d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ═══ 3a. DAILY BRAIN — text left, image right ═══ */}
      <section id="s-daily" className="py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-[5fr_6fr] gap-10 lg:gap-16 items-center">
            <div>
              <Reveal><Label>Daily intelligence</Label></Reveal>
              <Reveal delay={0.04}>
                <h2 className="text-2xl md:text-3xl lg:text-[2.2rem] font-bold tracking-[-0.015em] leading-[1.12]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Each morning, Kindra shows what matters today.
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-5 text-[14px] text-slate-400 leading-[1.7] max-w-[380px]">
                  Not generic tips. Cross-condition AI insights that connect medications, symptoms, and history in ways you wouldn't see yourself.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="mt-7 space-y-3">
                  <Bullet>Personalised to this user's conditions and medications</Bullet>
                  <Bullet>Generated from real health data — not templates</Bullet>
                  <Bullet>Updated daily based on recent activity</Bullet>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Shot src="/pres-assets/shot_daily.png" alt="Kindra daily view — Good morning Michael, mood 8, sleep 5.5h, energy 6, AI insights about ADHD-Bipolar interaction, myocarditis-cardiac connection, chest pain safety signal" />
            </Reveal>
          </div>
        </div>
      </section>


      {/* ═══ 3b. CHAT — image left, text right ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-[6fr_5fr] gap-10 lg:gap-16 items-center">
            <Reveal>
              <Shot src="/pres-assets/shot_chat.png" alt="User asks can I take aspirin — Kindra responds with personalised guidance referencing myocarditis, cardiac events, ulcers, lithium interactions" />
            </Reveal>
            <div>
              <Reveal><Label>Contextual guidance</Label></Reveal>
              <Reveal delay={0.04}>
                <h2 className="text-2xl md:text-3xl lg:text-[2.2rem] font-bold tracking-[-0.015em] leading-[1.12]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  "Can I take aspirin?"
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-5 text-[14px] text-slate-400 leading-[1.7] max-w-[380px]">
                  This user has myocarditis, cardiac events, active ulcers, and is on lithium. Kindra's answer isn't generic — it's specific, contextual, and potentially life-saving.
                </p>
                <p className="mt-4 text-[13px] text-slate-500 leading-[1.7] max-w-[380px]">
                  Not a chatbot. A health companion that remembers everything, connects conditions, and gives guidance a generic AI never could.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>


      {/* ═══ 3c. HEALTH PROFILE — text left, image right ═══ */}
      <section className="py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-[5fr_6fr] gap-10 lg:gap-16 items-center">
            <div>
              <Reveal><Label>Structured record</Label></Reveal>
              <Reveal delay={0.04}>
                <h2 className="text-2xl md:text-3xl lg:text-[2.2rem] font-bold tracking-[-0.015em] leading-[1.12]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  One connected health record that follows the user.
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-5 text-[14px] text-slate-400 leading-[1.7] max-w-[380px]">
                  Conditions, medications, symptoms, and care team — all connected in one persistent profile that grows with every interaction.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="mt-7 space-y-3">
                  <Bullet>Linked context across conditions, medications, and symptoms</Bullet>
                  <Bullet>Longitudinal memory that compounds over months and years</Bullet>
                  <Bullet>Clearer visits and better-informed decisions</Bullet>
                </div>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <Shot src="/pres-assets/shot_health.png" alt="Health profile — Bipolar I Disorder severe, ADHD, lithium 1350mg, chest pain, poor sleep, Dr Lisa Wong Endocrinologist, care team" />
            </Reveal>
          </div>
        </div>
      </section>


      {/* ═══ 3d. COURSES — image left, text right ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid lg:grid-cols-[6fr_5fr] gap-10 lg:gap-16 items-center">
            <Reveal>
              <Shot src="/pres-assets/shot_course.png" alt="Personalised health course — step-by-step guided learning about managing conditions, referencing the user's specific medications and symptoms" />
            </Reveal>
            <div>
              <Reveal><Label>Personalised learning</Label></Reveal>
              <Reveal delay={0.04}>
                <h2 className="text-2xl md:text-3xl lg:text-[2.2rem] font-bold tracking-[-0.015em] leading-[1.12]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Kindra turns health questions into structured action.
                </h2>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="mt-5 text-[14px] text-slate-400 leading-[1.7] max-w-[380px]">
                  Users ask any question. Kindra generates a complete course — step by step, referencing their actual conditions and care team. Each section ends with actionable items.
                </p>
                <p className="mt-4 text-[13px] text-slate-500 leading-[1.7] max-w-[380px]">
                  Education becomes action. Action becomes habit. Habit improves outcomes.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>


      {/* ═══ 4. WHY IT WINS — DARK ═══ */}
      <section className="bg-[#0F172A] text-white py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <p className="text-[11px] text-white/20 tracking-[0.2em] uppercase font-medium mb-4">Defensibility</p>
            <Heading className="max-w-lg text-white">Why this becomes hard to replace.</Heading>
          </Reveal>
          <div className="mt-16 md:mt-24 grid md:grid-cols-5 gap-x-10 gap-y-10">
            {[
              ['Personalised AI', 'Every response considers the full health picture — conditions, medications, history, and care team.'],
              ['Longitudinal memory', 'Health data compounds. After months, the system becomes irreplaceable. Years of structured context.'],
              ['Daily engagement', 'Built into daily life through insights, prompts, actions, and progress tracking.'],
              ['Behaviour change', 'Courses become actions. Actions become habits. Habits measurably improve outcomes.'],
              ['Life management', 'Moves beyond information delivery into continuous, active health support.'],
            ].map(([t, d], i) => (
              <Reveal key={i} delay={0.04 * i}>
                <h3 className="text-[14px] font-semibold text-white/90 mb-2.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h3>
                <p className="text-[12px] text-white/30 leading-[1.7]">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* ═══ 5. DIFFERENTIATION ═══ */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <Reveal><Label>Differentiation</Label></Reveal>
          <Reveal delay={0.04}>
            <Heading className="max-w-xl">Others store data. Kindra interprets, guides, and evolves.</Heading>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 md:mt-20 overflow-x-auto">
              <table className="w-full min-w-[580px] text-[13px]">
                <thead>
                  <tr>
                    <th className="text-left pb-4 border-b border-slate-100 text-slate-300 font-medium w-[200px]" />
                    <th className="pb-4 border-b border-slate-100 text-slate-300 font-medium text-center w-[80px]">Trackers</th>
                    <th className="pb-4 border-b border-slate-100 text-slate-300 font-medium text-center w-[80px]">Telehealth</th>
                    <th className="pb-4 border-b border-slate-100 text-slate-300 font-medium text-center w-[80px]">Records</th>
                    <th className="pb-4 border-b border-slate-100 text-slate-300 font-medium text-center w-[80px]">AI chat</th>
                    <th className="pb-4 border-b border-b-[#235347]/30 border-slate-100 text-[#235347] font-semibold text-center w-[80px]">Kindra</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Continuous health memory', [0,0,0,0,1]],
                    ['Cross-condition intelligence', [0,0,0,0,1]],
                    ['Personalised guidance', [0,1,0,0,1]],
                    ['Document understanding', [0,0,1,0,1]],
                    ['Pattern detection', [1,0,0,0,1]],
                    ['Between-visit support', [0,0,0,0,1]],
                    ['Structured health courses', [0,0,0,0,1]],
                  ].map(([label, vals], i) => (
                    <tr key={i}>
                      <td className="py-3 pr-4 border-b border-slate-50 text-slate-500">{label}</td>
                      {vals.map((v, j) => (
                        <td key={j} className={`py-3 border-b border-slate-50 text-center ${j === 4 ? '' : ''}`}>
                          {v ? <Check size={14} weight="bold" className={`inline ${j === 4 ? 'text-[#235347]' : 'text-slate-300'}`} /> : <span className="text-slate-150" style={{ color: '#e2e8f0' }}>—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ═══ 6. ENGAGEMENT LOOP ═══ */}
      <section className="bg-[#FAFBFC] border-y border-black/[0.04] py-20 md:py-28">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <Reveal><Label>Retention</Label></Reveal>
          <Reveal delay={0.04}>
            <Heading className="max-w-xl">The more a user engages, the more valuable Kindra becomes.</Heading>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-14 md:mt-20 flex flex-wrap items-center gap-4 md:gap-6 lg:gap-8">
              {['Daily Brain', 'Chat', 'Courses', 'Actions', 'Progress'].map((l, i) => (
                <React.Fragment key={i}>
                  <span className="text-[15px] md:text-base text-[#0F172A] font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{l}</span>
                  {i < 4 && <span className="text-slate-200 text-sm select-none">&#8594;</span>}
                </React.Fragment>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-8 text-[14px] text-slate-400 leading-[1.7] max-w-lg">
              More engagement creates more data. More data creates deeper personalisation. Deeper personalisation creates trust and reliance. This compounds — it is the core retention engine.
            </p>
          </Reveal>
        </div>
      </section>


      {/* ═══ 7. COMMERCIAL OPPORTUNITY ═══ */}
      <section id="s-commercial" className="py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <Reveal><Label>Commercial opportunity</Label></Reveal>
          <Reveal delay={0.04}>
            <Heading className="max-w-2xl">A category-defining platform with multiple paths to scale.</Heading>
          </Reveal>

          {/* Stats */}
          <div className="mt-14 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-14">
            {[
              ['1 in 2', 'adults live with a chronic condition globally'],
              ['Growing', 'multi-condition management is the new norm'],
              ['Zero', 'continuous support between appointments today'],
              ['Daily', 'engagement category — habits, not visits'],
            ].map(([stat, desc], i) => (
              <Reveal key={i} delay={0.04 * i}>
                <p className="text-2xl md:text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{stat}</p>
                <p className="mt-1.5 text-[12px] text-slate-400 leading-relaxed">{desc}</p>
              </Reveal>
            ))}
          </div>

          {/* Revenue channels */}
          <div className="mt-20 md:mt-24 border-t border-black/[0.04] pt-16 md:pt-20">
            <Reveal>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>Revenue channels</h3>
            </Reveal>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-14 gap-y-8">
              {[
                ['Direct subscriptions', 'Monthly recurring for individuals. Core intelligence layer.'],
                ['Clinics and medical centres', 'Patient continuity. Pre-appointment structured briefs.'],
                ['Hospitals', 'Post-discharge monitoring. Reduced readmission risk.'],
                ['Insurance companies', 'Better outcomes through continuous engagement.'],
                ['Government / NDIS', 'Scalable chronic condition management at population level.'],
                ['Enterprise / white-label', 'Platform integration for health organisations. API-driven.'],
              ].map(([t, d], i) => (
                <Reveal key={i} delay={0.03 * i}>
                  <h4 className="text-[14px] font-semibold text-[#0F172A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{t}</h4>
                  <p className="text-[12px] text-slate-400 leading-relaxed">{d}</p>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Revenue projections */}
          <div className="mt-20 md:mt-24 border-t border-black/[0.04] pt-16 md:pt-20">
            <Reveal>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-10" style={{ fontFamily: 'Outfit, sans-serif' }}>Illustrative revenue — consumer subscription only</h3>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl">
                {[
                  ['Early traction', '10K', '$1.8M'],
                  ['Growth', '50K', '$9M'],
                  ['Scale', '100K', '$18M'],
                ].map(([stage, users, rev], i) => (
                  <div key={i} className="bg-[#FAFBFC] border border-black/[0.04] rounded-lg p-5 md:p-6 text-center">
                    <p className="text-[10px] text-slate-300 tracking-[0.15em] uppercase font-medium mb-3">{stage}</p>
                    <p className="text-2xl md:text-[2rem] font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{rev}</p>
                    <p className="text-[11px] text-slate-400 mt-1">{users} users · $15/mo</p>
                  </div>
                ))}
              </div>
              <p className="mt-5 text-[11px] text-slate-300">B2B, clinical, and enterprise channels not modelled.</p>
            </Reveal>
          </div>
        </div>
      </section>


      {/* ═══ 8. TRANSFORMATION — DARK ═══ */}
      <section className="bg-[#0F172A] text-white py-20 md:py-28 lg:py-32">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <Heading className="max-w-lg text-white">Life without Kindra vs.&nbsp;life with Kindra.</Heading>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-16 md:mt-24 grid md:grid-cols-2 gap-16 md:gap-20 lg:gap-32">
              <div>
                <p className="text-[11px] text-white/15 tracking-[0.2em] uppercase font-medium mb-8">Without</p>
                <div className="space-y-6">
                  {[
                    'You forget what changed since your last visit.',
                    'You repeat your health story at every new provider.',
                    'You don\'t understand how your conditions interact.',
                    'You have no one to ask when something feels wrong.',
                    'You walk into appointments unprepared.',
                  ].map((t, i) => (
                    <p key={i} className="text-[14px] text-white/25 leading-relaxed">{t}</p>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] text-[#235347] tracking-[0.2em] uppercase font-semibold mb-8">With Kindra</p>
                <div className="space-y-6">
                  {[
                    'Every change is tracked and connected automatically.',
                    'One continuous record follows you between providers.',
                    'AI explains how your conditions and medications interact.',
                    'An intelligent companion is always available when you need it.',
                    'Structured preparation means clearer visits and better outcomes.',
                  ].map((t, i) => (
                    <p key={i} className="text-[14px] text-white/65 leading-relaxed">{t}</p>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      {/* ═══ 9. FINAL CLOSE ═══ */}
      <section className="py-28 md:py-40 lg:py-48">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] font-bold tracking-[-0.02em] leading-[1.08] max-w-2xl mx-auto" style={{ fontFamily: 'Outfit, sans-serif' }}>
              A real product solving a real global problem.
            </h2>
          </Reveal>
          <Reveal delay={0.06}>
            <p className="mt-5 text-base text-slate-400 max-w-md mx-auto leading-relaxed">
              Live in production. Built for real users. Positioned to scale.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 flex flex-wrap justify-center items-center gap-3">
              <a href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Investor%20Inquiry" className="bg-[#0F172A] text-white pl-6 pr-5 py-[13px] rounded-lg text-[13px] font-medium hover:bg-slate-800 transition-colors inline-flex items-center gap-2">
                Talk to the founder <ArrowUpRight size={14} />
              </a>
              <a href="mailto:hello@kindraai.dev?subject=Kindra%20—%20Investor%20Brief%20Request" className="text-slate-400 px-5 py-[13px] text-[13px] font-medium hover:text-slate-600 transition-colors">
                Request investor brief
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-24">
              <img src="/kindra-logo.png" alt="" className="h-5 mx-auto object-contain opacity-10" />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default PresentationPage;
